import React, { useState, useCallback, useEffect } from "react";
import "./ExpenseType.css";
import { FaPlus } from "react-icons/fa";
import { FaSpinner } from "react-icons/fa";
import editIcon from "../../assets/icons/edit.png";
import backIcon from "../../assets/icons/back.png";

import useAuthStore from "../../services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../services/toaster";
import { jwtDecode } from "jwt-decode";
import { useRef } from "react";
import axios from "axios";

export default function ExpenseType() {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [withSap, setWithSap] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;

  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(false);
  const getTokenCurrency = () => {
    const token = useAuthStore.getState().token;
    if (!token) return "";

    const user = jwtDecode(token);
    return user.currency || "";
  };
  const [formData, setFormData] = useState({
    expensetypename: "",
    expensetypedescription: "",
    glcode: "",
    costcenter: "",
    currency: getTokenCurrency(),
    isattachmentrequired: 1,
    istaxable: 0,
    isactive: 1,
  });

  // FETCH
  const fetchExpenseTypes = useCallback(async () => {
    try {
      setLoading(true);
      const token = useAuthStore.getState().token;
      if (!token) return;

      const user = jwtDecode(token);
      const company_id = user.company_id;

      const response = await axios.get(
        `${API_URL}/expense-type/${company_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const formattedData = response.data.map((item) => ({
        id: item.id,
        type: item.expensetypename,
        gl: item.glcode,
        currency: item.currency,
        cost: item.costcenter,
        attachment: item.isattachmentrequired ? "Required" : "Optional",
        taxable: item.istaxable ? "Yes" : "No",
        status: item.isactive === 1,
      }));
      setTableData(formattedData);
    } catch (error) {
      console.error("Error fetching expense types:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchExpenseTypes();
  }, [fetchExpenseTypes]);

  // INPUT CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    if (loading) return;

    if (!formData.expensetypename.trim()) {
      toastWarning("Expense Type is required");
      return;
    }

    if (withSap === 1) {
      if (!formData.glcode.trim()) {
        alert("GL Code is required");
        return;
      }

      if (!formData.costcenter.trim()) {
        alert("Cost Center is required");
        return;
      }
    }
    if (!formData.currency) {
      alert("Currency is required");
      return;
    }
    try {
      setLoading(true);

      const token = useAuthStore.getState().token;
      if (!token) return;

      const user = jwtDecode(token);
      const company_id = user.company_id;

      const payload = {
        expensetypename: formData.expensetypename,
        expensetypedescription: formData.expensetypedescription,
        glcode: formData.glcode,
        costcenter: formData.costcenter,
        currency: formData.currency,
        isactive: Number(formData.isactive),
        isattachmentrequired: Number(formData.isattachmentrequired),
        istaxable: Number(formData.istaxable),
      };

      if (isEditing) {
        const res = await axios.put(
          `${API_URL}/expense-type/${company_id}/${editId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (Array.isArray(res.data)) {
          const formattedData = res.data.map((item) => ({
            id: item.id,
            type: item.expensetypename,
            gl: item.glcode,
            cost: item.costcenter,
            currency: item.currency,
            attachment:
              item.isattachmentrequired === 1 ? "Required" : "Optional",
            taxable: item.istaxable === 1 ? "Yes" : "No",
            status: item.isactive === 1,
          }));

          setTableData(formattedData);
        }

        toastSuccess("Expense Type Updated Successfully");
        handleCancel();
      } else {
        const res = await axios.post(
          `${API_URL}/expense-type/${company_id}`,
          {
            ...payload,
            company_id,
            createdby: user.user_id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (Array.isArray(res.data)) {
          const formattedData = res.data.map((item) => ({
            id: item.id,
            type: item.expensetypename,
            gl: item.glcode,
            cost: item.costcenter,
            currency: item.currency,
            attachment:
              item.isattachmentrequired === 1 ? "Required" : "Optional",
            taxable: item.istaxable === 1 ? "Yes" : "No",
            status: item.isactive === 1,
          }));

          setTableData(formattedData);
        } else {
          const item = res.data;

          const formattedItem = {
            id: item.id,
            type: item.expensetypename,
            gl: item.glcode,
            cost: item.costcenter,
            currency: item.currency,
            attachment:
              item.isattachmentrequired === 1 ? "Required" : "Optional",
            taxable: item.istaxable === 1 ? "Yes" : "No",
            status: item.isactive === 1,
          };

          setTableData((prev) => [formattedItem, ...prev]);
        }
        toastSuccess("Expense Type Created Successfully");
        handleCancel();
      }
    } catch (error) {
      toastError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setShowForm(true);
    setIsEditing(true);
    setEditId(item.id);

    setFormData({
      expensetypename: item.type,
      glcode: item.gl,
      costcenter: item.cost,
      currency: item.currency || "",
      isattachmentrequired: item.attachment === "Required" ? 1 : 0,
      istaxable: item.taxable === "Yes" ? 1 : 0,
      isactive: item.status ? 1 : 0,
    });
  };

  const handleToggle = async (item) => {
    const token = useAuthStore.getState().token;
    if (!token) return;

    const user = jwtDecode(token);
    const company_id = user.company_id;

    const newStatus = item.status ? 0 : 1;

    setTableData((prev) =>
      prev.map((row) =>
        row.id === item.id ? { ...row, status: newStatus === 1 } : row,
      ),
    );

    try {
      await axios.put(
        `${API_URL}/expense-type/${company_id}/${item.id}`,
        {
          expensetypename: item.type,
          expensetypedescription: item.type,
          glcode: item.gl,
          costcenter: item.cost,
          currency: item.currency || "",
          isactive: newStatus,
          isattachmentrequired: item.attachment === "Required" ? 1 : 0,
          istaxable: item.taxable === "Yes" ? 1 : 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error("Toggle failed:", error);
      setTableData((prev) =>
        prev.map((row) =>
          row.id === item.id ? { ...row, status: item.status } : row,
        ),
      );
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditId(null);

    setFormData({
      expensetypename: "",
      glcode: "",
      costcenter: "",
      currency: getTokenCurrency(),
      isattachmentrequired: 1,
      istaxable: 0,
      isactive: 1,
    });
  };

  const getWithSap = () => {
    const token = useAuthStore.getState().token;
    if (!token) return 0;

    const user = jwtDecode(token);
    return Number(user.withsap) || 0;
  };

  useEffect(() => {
    setWithSap(getWithSap());
  }, []);

  if (loading) {
    return (
      <div className="expense-loader-container">
        <div className="expense-center-loader">
          <FaSpinner className="expense-spinner" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="expense-container">
      {!showForm ? (
        <>
          <div className="expense-header">
            <button
              className="expense-add-btn"
              onClick={() => {
                handleCancel();
                setShowForm(true);
              }}
            >
              <FaPlus /> Add Expense Type
            </button>
          </div>

          <div className="expense-table-wrapper">
            <table className="expense-table">
              <thead>
                <tr>
                  <th>Expense Type</th>
                  <th>GL Code</th>
                  <th>Cost Center</th>
                  <th>Default Currency</th>
                  <th>Attachment</th>
                  <th>Taxable</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="expense-no-data">
                      No Data Available
                    </td>
                  </tr>
                ) : (
                  tableData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{item.type}</td>
                      <td>{item.gl}</td>
                      <td>{item.cost}</td>
                      <td>{item.currency}</td>
                      <td>{item.attachment}</td>
                      <td>{item.taxable}</td>
                      <td>
                        <label className="expense-switch">
                          <input
                            type="checkbox"
                            checked={item.status}
                            onChange={() => handleToggle(item)}
                          />
                          <span className="expense-slider"></span>
                        </label>
                      </td>
                      <td>
                        <button
                          className="expense-edit-btn"
                          onClick={() => handleEdit(item, index)}
                        >
                          <img
                            src={editIcon}
                            alt="edit"
                            className="expense-edit-icon"
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="expense-form-page">
          <div className="expense-form-content">
            {/* HEADER */}
            <div className="expense-form-header">
              <button
                className="expense-circle-back-btn"
                onClick={handleCancel}
              >
                <img src={backIcon} alt="back" className="expense-back-icon" />
              </button>
              <h3>{isEditing ? "Update Expense Type" : "Add Expense Type"}</h3>
            </div>

            {/* BODY */}
            <div className="expense-scroll-inner-body">
              {/* ROW 1 - 4 FIELDS */}
              <div className="expense-form-row-4">
                <div className="expense-form-group">
                  <label>
                    Expense Type{" "}
                    <span className="expense-required-star">*</span>
                  </label>
                  <input
                    name="expensetypename"
                    placeholder="Expense Type Name"
                    value={formData.expensetypename}
                    onChange={handleChange}
                  />
                </div>

                <div className="expense-form-group">
                  <label>
                    GL Account Code{" "}
                    {withSap === 1 && (
                      <span className="expense-required-star">*</span>
                    )}
                  </label>
                  <input
                    name="glcode"
                    placeholder="GL Account Code"
                    value={formData.glcode}
                    onChange={handleChange}
                  />
                </div>

                <div className="expense-form-group">
                  <label>
                    Cost Center
                    {withSap === 1 && (
                      <span className="expense-required-star">*</span>
                    )}
                  </label>
                  <input
                    name="costcenter"
                    placeholder="Cost Center"
                    value={formData.costcenter}
                    onChange={handleChange}
                  />
                </div>

                <div className="expense-form-group">
                  <label>
                    Default Currency{" "}
                    <span className="expense-required-star">*</span>
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                  >
                    <option value="">Select Currency</option>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </div>

              {/* ROW 2 - 3 FIELDS */}
              <div className="expense-form-row-3">
                <div className="expense-form-group">
                  <label>Required Attachment</label>
                  <select
                    name="isattachmentrequired"
                    value={formData.isattachmentrequired}
                    onChange={handleChange}
                  >
                    <option value={1}>Required</option>
                    <option value={0}>Optional</option>
                  </select>
                </div>

                <div className="expense-form-group">
                  <label>Taxable</label>
                  <select
                    name="istaxable"
                    value={formData.istaxable}
                    onChange={handleChange}
                  >
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                  </select>
                </div>

                <div className="expense-form-group">
                  <label>Status</label>
                  <select
                    name="isactive"
                    value={formData.isactive}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="expense-form-actions">
              <button className="expense-cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="expense-save-btn"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : isEditing ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
